import { FilteredItemProps } from '@delmonicos/shared-design-system-react/src/components/filtered-item/filtered-item';
import { GetEmspUsersPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/emsp-bo/models/inputs/emsp-users/get-emsp-users-paginated.model';
import { EmspUserModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/back-office-gateway-emsp-lib/src/outputs/emsp-users/emsp-user.model';
import {
  ColumnDef,
  ColumnOrderState,
  VisibilityState,
  createColumnHelper,
} from '@tanstack/react-table';

import { EmspBoEmspUserBillingFrequencyFilterStateController } from '../emsp-bo-emsp-user-billing-frequency-filter-state/emsp-bo-emsp-user-billing-frequency-filter-state.controller';
import { EmspBoEmspUserListEmailAddressColumn } from './emsp-bo-emsp-user-list-email-address.column';
import { EmspBoEmspUserListFirstNameColumn } from './emsp-bo-emsp-user-list-first-name.column';
import { EmspBoEmspUserListIsEmailAddressVerifiedColumn } from './emsp-bo-emsp-user-list-is-email-address-verified.column';
import { EmspBoEmspUserListIsWebauthnEnabledColumn } from './emsp-bo-emsp-user-list-is-webauthn-enabled.column';
import { EmspBoEmspUserListLastConnectionColumn } from './emsp-bo-emsp-user-list-last-connection.column';
import { EmspBoEmspUserListLastNameColumn } from './emsp-bo-emsp-user-list-last-name.column';
import { EmspBoEmspUserListPseudoColumn } from './emsp-bo-emsp-user-list-pseudo.column';

// /////////////////////////////////////////////////////////////////////////////
// SORTING
// /////////////////////////////////////////////////////////////////////////////

export const EMSP_BO_EMSP_USER_LIST_SORT_FIELD: NonNullable<
  GetEmspUsersPaginatedModel['sortField']
> = 'lastConnection';

export const EMSP_BO_EMSP_USER_LIST_SORT_ORDER: NonNullable<
  GetEmspUsersPaginatedModel['sortOrder']
> = -1;

// /////////////////////////////////////////////////////////////////////////////
// FILTERING
// /////////////////////////////////////////////////////////////////////////////

type ResourceListFilter = Partial<
  Record<
    keyof GetEmspUsersPaginatedModel,
    {
      componentType: React.ComponentType<{
        value: any;
        onRemove: FilteredItemProps['onRemove'];
      }>;
    }
  >
>;

export const EMSP_BO_EMSP_USER_LIST_FILTER: ResourceListFilter = {
  billingFrequency: {
    componentType: EmspBoEmspUserBillingFrequencyFilterStateController,
  },
} as const;

export const EMSP_BO_EMSP_USER_LIST_FILTER_NAME_LIST = Object.keys(
  EMSP_BO_EMSP_USER_LIST_FILTER,
) as Array<keyof typeof EMSP_BO_EMSP_USER_LIST_FILTER>;

// /////////////////////////////////////////////////////////////////////////////
// DISPLAYING
// /////////////////////////////////////////////////////////////////////////////

export const emspBoEmspUserListColumnHelper =
  createColumnHelper<EmspUserModel>();

type ResourceColumnType = ColumnDef<EmspUserModel, any>[];

export const EMSP_BO_EMSP_USER_LIST_COLUMN_LIST: ResourceColumnType = [
  emspBoEmspUserListColumnHelper.accessor(
    'pseudo',
    EmspBoEmspUserListPseudoColumn,
  ),
  emspBoEmspUserListColumnHelper.accessor(
    'lastName',
    EmspBoEmspUserListLastNameColumn,
  ),
  emspBoEmspUserListColumnHelper.accessor(
    'firstName',
    EmspBoEmspUserListFirstNameColumn,
  ),
  emspBoEmspUserListColumnHelper.accessor(
    'emailAddress',
    EmspBoEmspUserListEmailAddressColumn,
  ),
  emspBoEmspUserListColumnHelper.accessor(
    'isEmailAddressVerified',
    EmspBoEmspUserListIsEmailAddressVerifiedColumn,
  ),
  emspBoEmspUserListColumnHelper.accessor(
    'isWebauthnEnabled',
    EmspBoEmspUserListIsWebauthnEnabledColumn,
  ),
  emspBoEmspUserListColumnHelper.accessor(
    'lastConnection',
    EmspBoEmspUserListLastConnectionColumn,
  ),
];

export const EMSP_BO_EMSP_USER_LIST_COLUMN_ID_LIST =
  EMSP_BO_EMSP_USER_LIST_COLUMN_LIST.flatMap((columnDef) => {
    if ('accessorKey' in columnDef) {
      return [columnDef.id ?? (columnDef.accessorKey as string)];
    }

    if (columnDef.id) {
      return [columnDef.id];
    }

    return [];
  });

export const EMSP_BO_EMSP_USER_LIST_COLUMN_ORDER = [
  ...EMSP_BO_EMSP_USER_LIST_COLUMN_ID_LIST,
];

export const EMSP_BO_EMSP_USER_LIST_COLUMN_VISIBILITY: VisibilityState =
  EMSP_BO_EMSP_USER_LIST_COLUMN_ID_LIST.reduce(
    (acc, id) => ({ ...acc, [id]: true }),
    {} as VisibilityState,
  );

// /////////////////////////////////////////////////////////////////////////////
// OPTIONS
// /////////////////////////////////////////////////////////////////////////////

export interface EmspBoEmspUserListOptionsModel {
  columnVisibility: VisibilityState;
  columnOrder: ColumnOrderState;
}

export const EMSP_BO_EMSP_USER_LIST_DEFAULT_OPTIONS: EmspBoEmspUserListOptionsModel =
  {
    columnVisibility: EMSP_BO_EMSP_USER_LIST_COLUMN_VISIBILITY,
    columnOrder: EMSP_BO_EMSP_USER_LIST_COLUMN_ORDER,
  };

export const EMSP_BO_EMSP_USER_LIST_COLUMN_LABEL_I18N_KEY: Record<
  string,
  string
> = {
  pseudo: 'delmonicos-shared-sdk.outputs-emsp-user.pseudo',
  lastName: 'delmonicos-shared-sdk.outputs-emsp-user.last-name',
  firstName: 'delmonicos-shared-sdk.outputs-emsp-user.first-name',
  emailAddress: 'delmonicos-shared-sdk.outputs-emsp-user.email-address',
  isEmailAddressVerified:
    'delmonicos-shared-sdk.outputs-emsp-user.is-email-address-verified',
  isWebauthnEnabled:
    'delmonicos-shared-sdk.outputs-emsp-user.is-webauthn-enabled',
  lastConnection: 'delmonicos-shared-sdk.outputs-emsp-user.last-connection',
};
